/**
 * API URL utilities for Learning Companion Frontend
 * Handles both proxy and direct backend URL configurations
 */

// Get backend URL from environment or default
const getBackendUrl = (): string => {
  // In development, use empty string to trigger proxy usage (setupProxy.js handles routing)
  if (process.env.NODE_ENV === 'development') {
    // Return empty string to use proxy prefix (/api)
    // This allows setupProxy.js to handle the backend routing
    return '';
  }
  
  // In production, always use environment variable or fallback to production URL
  return process.env.REACT_APP_API_BASE_URL || 'https://learning-companion-be-dev.data-nonprod.edgenuity.com';
};

// Get API URL for direct fetch calls (used by components)
export const getApiUrl = (endpoint: string): string => {
  const baseUrl = getBackendUrl();
  
  // Check if endpoint already contains a full URL (starts with http:// or https://)
  if (endpoint.startsWith('http://') || endpoint.startsWith('https://')) {
    return endpoint; // Return as-is if it already contains the full URL
  }
  
  // Check if endpoint already contains the current base URL
  if (baseUrl && endpoint.includes(baseUrl)) {
    return endpoint; // Return as-is if it already contains the base URL
  }
  
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  // In development (baseUrl empty), use /api proxy prefix
  // In production (baseUrl set), use direct backend URL
  return baseUrl ? `${baseUrl}${cleanEndpoint}` : `/api${cleanEndpoint}`;
};

// Build API URL with proper prefix
export const buildApiUrl = (endpoint: string): string => {
  const backendUrl = getBackendUrl();
  
  // Remove leading slash from endpoint if present
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
  
  if (backendUrl === '') {
    // Use proxy prefix
    return `/api/${cleanEndpoint}`;
  } else {
    // Use direct backend URL
    return `${backendUrl}/${cleanEndpoint}`;
  }
};

// Build WebSocket URL for WebSocket connections
export const getWebSocketUrl = (endpoint: string): string => {
  // Remove leading slash from endpoint if present
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
  
  if (process.env.NODE_ENV === 'development') {
    // In development, connect directly to backend WebSocket
    // Use ws:// for localhost development
    return `ws://localhost:8000/${cleanEndpoint}`;
  } else {
    // In production, use current host with appropriate WebSocket protocol
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}/api/${cleanEndpoint}`;
  }
};

// Enhanced fetch with error handling and retry logic
export const apiRequest = async (
  endpoint: string, 
  options: RequestInit = {}
): Promise<Response> => {
  const maxRetries = 2;
  let lastError: Error | undefined;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      let url: string;
      
      if (attempt === 0) {
        // First attempt: use proxy if in development, direct URL in production
        url = buildApiUrl(endpoint);
      } else if (process.env.NODE_ENV === 'development') {
        // Retry in development: try direct localhost backend
        const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
        url = `http://localhost:8000/${cleanEndpoint}`;
      } else {
        // Retry in production: try production backend URL
        const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
        url = `https://learning-companion-be-dev.data-nonprod.edgenuity.com/${cleanEndpoint}`;
      }
      
      console.log(`API Request (attempt ${attempt + 1}): ${options.method || 'GET'} ${url}`);
      
      const response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });
      
      if (response.ok) {
        console.log(`API Request successful: ${response.status} ${url}`);
        return response;
      } else {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }
      
    } catch (error) {
      lastError = error as Error;
      console.warn(`API Request failed (attempt ${attempt + 1}):`, lastError.message);
      
      // If this is the last attempt, throw the error
      if (attempt === maxRetries) {
        throw lastError;
      }
      
      // Wait a bit before retrying
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  }
  
  throw new Error(lastError?.message || 'Request failed after retries');
};

// API endpoints configuration
export const API_ENDPOINTS = {
  // Story Context
  STORY_CONTEXT_UPDATE: 'story-context/update-context',
  STORY_CONTEXT_ANNOUNCEMENT: 'story-context/get-announcement', 
  STORY_CONTEXT_CLEAR: 'story-context/clear-context',
  STORY_CONTEXT_MONITOR: 'story-context/monitor-topic',
  
  // Stories
  STORIES_LIST: 'stories/',
  STORIES_BY_ID: 'stories',
  
  // Configuration
  CONFIG_SESSION: 'config/session',
  CONFIG_WEBSOCKET_URL: 'config/websocket-url',
  CONFIG_TRIGGER_INTRO: 'config/trigger-introduction',
  
  // Health
  HEALTH_CHECK: 'health',
  HEALTH_AZURE: 'health/azure',
  HEALTH_DETAILED: 'health/detailed',
  
  // Version
  VERSION: 'version',
  VERSION_DETAILED: 'version/detailed',
  
  // UI State
  UI_STATE_UPDATE: 'ui-state/update',
  UI_STATE_VOICELIVE_MESSAGE: 'ui-state/voicelive-message',
} as const;

// Helper functions for common API patterns
export const postJson = async (endpoint: string, data: any): Promise<Response> => {
  return apiRequest(endpoint, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const getJson = async (endpoint: string): Promise<Response> => {
  return apiRequest(endpoint, {
    method: 'GET',
  });
};

export const putJson = async (endpoint: string, data: any): Promise<Response> => {
  return apiRequest(endpoint, {
    method: 'PUT', 
    body: JSON.stringify(data),
  });
};

// Debug function to test API connectivity
export const testApiConnectivity = async (): Promise<{proxy: boolean, direct: boolean}> => {
  const results = {proxy: false, direct: false};
  
  try {
    // In development, use proxy; in production, use /api prefix (nginx proxies to backend)
    const proxyUrl = process.env.NODE_ENV === 'development' ? '/api/health' : '/api/health';
    const proxyResponse = await fetch(proxyUrl, {method: 'GET'});
    results.proxy = proxyResponse.ok;
  } catch (error) {
    console.log('Proxy test failed:', error);
  }
  
  try {
    // Test direct localhost backend (only in development)
    if (process.env.NODE_ENV === 'development') {
      const directResponse = await fetch('http://localhost:8000/health', {method: 'GET'});
      results.direct = directResponse.ok;
    }
  } catch (error) {
    console.log('Direct backend test failed:', error);
  }
  
  console.log('API Connectivity Test Results:', results);
  return results;
};

// Ensure this file is treated as a module for TypeScript isolatedModules
export {};